import { ErrorModal, TreeNode, TreePickerControlV2 } from "@dexteel/mesf-core";
import {
	CircularProgress,
	FormControl,
	FormHelperText,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { FieldError } from "react-hook-form";
import { DelayCode, TreeCode } from "../models/DelayCode";
import { getDelayCodes } from "../repositories/DelayCodeRepository";

/**
 * Filter modes for "Passing the Ball" workflow:
 * - "all": Show all codes (default behavior)
 * - "branches-only": Only show branch codes (codes with BranchPermissionAction set)
 * - "children-of": Only show children of a specific branch code
 */
export type DelayCodeFilterMode = "all" | "branches-only" | "children-of";

const buildTreeCodes = (nodes: TreeCode[]): TreeNode | null => {
	if (!nodes || nodes.length === 0) {
		return null;
	}

	// Build id mapping
	const idMapping = nodes.reduce(
		(acc: Record<number, number>, el: TreeCode, i: number) => {
			acc[el.id] = i;
			return acc;
		},
		{},
	);

	// Find all top-level nodes (parentId is null or parent not in list)
	const topLevelNodes: TreeCode[] = [];

	// Build parent-child relationships
	nodes.forEach((el: TreeCode) => {
		if (el.parentId === null || idMapping[el.parentId] === undefined) {
			topLevelNodes.push(el);
		} else {
			const parentEl = nodes[idMapping[el.parentId]];
			if (parentEl) {
				parentEl.children = [...(parentEl.children || []), el];
			}
		}
	});

	// If only one top-level node, use it as root; otherwise create virtual root
	let root: TreeCode;
	if (topLevelNodes.length === 1) {
		root = topLevelNodes[0];
	} else if (topLevelNodes.length > 1) {
		root = {
			id: -1,
			parentId: null,
			name: "All Codes",
			isLeaf: false,
			isActive: true,
			isSelectable: false,
			children: topLevelNodes,
			sortOrder: 0,
		};
	} else {
		return null;
	}

	// @ts-ignore - TreeNode type from mesf-core is compatible
	return root;
};

const useDelayCodes = (
	assetId: number | null,
	onError?: (error: Error) => void,
) => {
	return useQuery<DelayCode[], Error>({
		queryKey: ["delayCodes", assetId],
		queryFn: () => getDelayCodes(assetId),
		onError: onError,
	});
};

/**
 * Filters delay codes based on the filter mode for "Passing the Ball" workflow.
 *
 * @param codes - All delay codes from the API
 * @param filterMode - The filter mode to apply
 * @param branchCodeId - Required when filterMode is "children-of"
 * @returns Filtered delay codes
 */
const filterDelayCodes = (
	codes: DelayCode[],
	filterMode: DelayCodeFilterMode,
	branchCodeId?: number | null,
): DelayCode[] => {
	if (filterMode === "all") {
		return codes;
	}

	if (filterMode === "branches-only") {
		// Only return codes that have BranchPermissionAction set (these are the branch codes)
		return codes.filter((code) => code.BranchPermissionAction !== null);
	}

	if (filterMode === "children-of" && branchCodeId) {
		// Return the branch code itself and all its descendants
		const branchCode = codes.find((c) => c.DelayCodeId === branchCodeId);
		if (!branchCode) return [];

		// Get all descendant IDs recursively
		const getDescendantIds = (parentId: number): number[] => {
			const children = codes.filter((c) => c.ParentDelayCodeId === parentId);
			const childIds = children.map((c) => c.DelayCodeId as number);
			const grandchildIds = childIds.flatMap((id) => getDescendantIds(id));
			return [...childIds, ...grandchildIds];
		};

		const descendantIds = new Set([
			branchCodeId,
			...getDescendantIds(branchCodeId),
		]);

		return codes.filter((code) =>
			descendantIds.has(code.DelayCodeId as number),
		);
	}

	return codes;
};

interface DelayCodeTreePickerProps {
	label?: string;
	value: number | null;
	onChange: (value: number | null, defaultCategoryId: number | null) => void;
	disabled?: boolean;
	assetId: number | null;
	fieldError?: FieldError | undefined;
	showFullPath?: boolean;
	/**
	 * Filter mode for "Passing the Ball" workflow:
	 * - "all": Show all codes (default)
	 * - "branches-only": Only show branch codes (for operators)
	 * - "children-of": Only show children of branchCodeId (for specialists)
	 */
	filterMode?: DelayCodeFilterMode;
	/**
	 * When filterMode is "children-of", specifies which branch to show children of.
	 */
	branchCodeId?: number | null;
}

export const DelayCodeTreePicker = ({
	label = "Code",
	value,
	onChange,
	disabled,
	assetId,
	fieldError,
	showFullPath = false,
	filterMode = "all",
	branchCodeId,
}: DelayCodeTreePickerProps) => {
	const [error, setError] = useState("");

	const { data, isLoading } = useDelayCodes(assetId, (error) =>
		setError(error.message),
	);

	// Apply filtering based on filterMode
	const filteredData = useMemo(() => {
		if (!data) return [];
		return filterDelayCodes(data, filterMode, branchCodeId);
	}, [data, filterMode, branchCodeId]);

	const rowsMapped = useMemo(
		() =>
			filteredData
				.filter((del: DelayCode) => del.DelayCodeId !== null)
				.map(
					(del: DelayCode) =>
						({
							id: del.DelayCodeId as number,
							parentId: del.ParentDelayCodeId,
							name: del.DelayCodeName,
							isLeaf: !del.IsBranch,
							isActive: true,
							isSelectable: del.IsSelectable,
							children: [],
							sortOrder: del.SortOrder,
							branchPermissionAction: del.BranchPermissionAction,
						}) as TreeCode,
				),
		[filteredData],
	);

	const delayCodes = useMemo(
		() => buildTreeCodes(rowsMapped || []),
		[rowsMapped],
	);

	if (isLoading) {
		return (
			<TextField
				disabled
				size="small"
				label={label as string}
				InputProps={{
					endAdornment: <CircularProgress color="inherit" size={20} />,
				}}
			/>
		);
	}

	// Handle case when data is empty or no root found
	if (delayCodes == null) {
		return (
			<TextField
				disabled
				size="small"
				label={label as string}
				value=""
				placeholder="No codes"
			/>
		);
	} else {
		return (
			<>
				<FormControl error={!!fieldError} fullWidth sx={{}}>
					<TreePickerControlV2
						inputTitle={label}
						title={label}
						dataSource={delayCodes}
						value={value || 0}
						onSelect={(value: number) => {
							const defaultCategoryId = (Array.isArray(data) ? data : []).find(
								(c) => c.DelayCodeId === value,
							)?.DelayCategoryId;
							onChange(value, defaultCategoryId ?? null);
						}}
						selectBranch
						selectActive={false}
						selectInternal={false}
						disabled={disabled}
						showClearButton
						showFullPath={showFullPath}
						mostUsedCount={5}
						itemsWithParent={[
							"F1",
							"F2",
							"F3",
							"F4",
							"F5",
							"F6",
							"F7",
							"R1",
							"R2",
							"R3",
							"R4",
						]}
						dialogMaxWidth="md"
					/>
					{fieldError && <FormHelperText>{fieldError.message}</FormHelperText>}
				</FormControl>
				<ErrorModal error={error} onHide={() => setError("")} />
			</>
		);
	}
};
